import { useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from "react";
import type { Property, Room } from "@shared/schema";
import { isUsefulPropertyKnowledgeBase, PROPERTY_SETUP_CORE_TOTAL, propertySetupCoreChecks } from "@shared/property-setup";
import { Link } from "wouter";
import { ChevronRight, CircleCheck, CircleDashed, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";

type ReadState = "loading" | "success" | "error" | "restricted";
type DeviceSummary = { locks?: unknown[]; thermostats?: unknown[] };
type DocumentSummary = { total?: number };
type ChecklistRoom = Room & { hasSavedImages?: boolean };

type ChecklistProps = {
  property: Property;
  propertyPhoto: string | null | undefined;
  photoState: ReadState;
  rooms: ChecklistRoom[] | undefined;
  roomsState: ReadState;
  knowledgeBase: string | null | undefined;
  knowledgeBaseState: ReadState;
  appliances: unknown[] | undefined;
  appliancesState: ReadState;
  devices: DeviceSummary | undefined;
  devicesState: ReadState;
  documents: DocumentSummary | undefined;
  documentsState: ReadState;
  expanded?: boolean;
  actions?: ReactNode;
  onExpandedChange?: (expanded: boolean) => void;
  onNavigate?: (href: string, event: MouseEvent<HTMLAnchorElement>) => void;
};

type ChecklistItem = {
  id: string;
  label: string;
  detail: string;
  done: boolean;
  href: string;
  optional?: boolean;
};

export const isUsefulKnowledgeBase = isUsefulPropertyKnowledgeBase;

function getRoomTitle(room: Room): string {
  return room.roomTitle?.trim() || "";
}

export function buildPropertySetupItems({
  property,
  propertyPhoto,
  rooms,
  knowledgeBase,
  appliances,
  devices,
  documents,
}: Pick<ChecklistProps, "property" | "propertyPhoto" | "rooms" | "knowledgeBase" | "appliances" | "devices" | "documents">): { core: ChecklistItem[]; optional: ChecklistItem[] } {
  const realRooms = (rooms ?? []).filter((room) => !room.shadowOfRoomId);
  const bedrooms = realRooms.filter((room) => room.roomType === "bedroom");
  const bathrooms = realRooms.filter((room) => room.roomType === "bathroom");
  const bathroomBedroomIds = new Set(bathrooms.flatMap((room) => room.associatedBedroomIds ?? []));
  const titledBedrooms = bedrooms.filter((room) => getRoomTitle(room).length > 0);
  const bedroomsWithImages = bedrooms.filter((room) => Boolean(room.hasSavedImages || room.inputImageUrl || room.outputImageUrl));
  const coreChecks = propertySetupCoreChecks({ ...property, knowledgeBase }, propertyPhoto, rooms);
  const core: ChecklistItem[] = [
    {
      id: "basic",
      label: "Basic property details",
      detail: coreChecks.basicDetails ? "Title, location, type, status, room and bathroom counts, and description are saved." :
        `Missing: ${[
          !property.name?.trim() && "title",
          !property.address?.trim() && "address",
          !property.managementType && "property type",
          !property.status && "status",
          !(Number(property.roomCount) > 0) && "positive room count",
          !(Number(property.bathroomCount) > 0) && "positive bathroom count",
          !property.description?.trim() && "description",
        ].filter(Boolean).join(", ")}.`,
      done: coreChecks.basicDetails,
      href: "#property-overview-fields",
    },
    {
      id: "photo",
      label: "Property photo",
      detail: propertyPhoto ? "A saved property photo is ready." : "Upload a clear photo of the home.",
      done: coreChecks.propertyPhoto,
      href: "#property-photo",
    },
    {
      id: "rooms",
      label: "Bedrooms and room details",
      detail: bedrooms.length === 0
        ? "Add real bedroom records with useful names."
        : `${titledBedrooms.length} of ${bedrooms.length} bedrooms have useful titles${bathrooms.length ? ` · ${bedrooms.filter((room) => bathroomBedroomIds.has(room.id)).length} linked to recorded bathrooms` : ""}.`,
      done: coreChecks.bedrooms,
      href: "?tab=rooms",
    },
    {
      id: "room-photos",
      label: "Bedroom photos",
      detail: bedrooms.length === 0 ? "Add bedrooms before their photos." : `${bedroomsWithImages.length} of ${bedrooms.length} bedrooms have photos.`,
      done: coreChecks.bedroomPhotos,
      href: "?tab=rooms",
    },
    {
      id: "wifi",
      label: "Wi-Fi details",
      detail: property.wifiName?.trim() && property.wifiPassword?.trim()
        ? "Network name and password are saved."
        : `Missing: ${[!property.wifiName?.trim() && "network name", !property.wifiPassword?.trim() && "password"].filter(Boolean).join(" and ")}.`,
      done: coreChecks.wifi,
      href: "#property-wifi",
    },
    {
      id: "waste",
      label: "Waste pickup",
      detail: property.trashPickupDay?.trim() ? "Trash pickup information is saved." : "Add the regular trash pickup day.",
      done: coreChecks.wastePickup,
      href: "#property-waste",
    },
    {
      id: "knowledge",
      label: "Knowledge Base",
      detail: coreChecks.knowledgeBase ? "Property-specific guidance is saved." : "Add useful house guidance, rules, or arrival information.",
      done: coreChecks.knowledgeBase,
      href: "?tab=knowledge-base",
    },
  ];
  const meaningfulAppliances = (appliances ?? []).filter((item) => {
    if (!item || typeof item !== "object") return false;
    const appliance = item as { propertyId?: number; applianceType?: string | null; make?: string | null; model?: string | null; serialNumber?: string | null };
    if (appliance.propertyId != null && appliance.propertyId !== property.id) return false;
    return Boolean(appliance.applianceType?.trim() || appliance.make?.trim() || appliance.model?.trim() || appliance.serialNumber?.trim());
  }).length;
  const belongsHere = (item: unknown) => !!item && typeof item === "object" &&
    ("propertyId" in item) && item.propertyId === property.id;
  const linkedLocks = devices?.locks?.filter(belongsHere).length ?? 0;
  const linkedThermostats = devices?.thermostats?.filter(belongsHere).length ?? 0;
  const optional: ChecklistItem[] = [
    { id: "appliances", label: "Appliance inventory", detail: meaningfulAppliances ? `${meaningfulAppliances} saved appliance${meaningfulAppliances === 1 ? "" : "s"}.` : "Add appliances with a name or identifying details.", done: meaningfulAppliances > 0, href: "?tab=appliances", optional: true },
    { id: "locks", label: "Smart locks", detail: linkedLocks ? `${linkedLocks} lock${linkedLocks === 1 ? "" : "s"} linked to this property.` : "No smart locks linked to this property.", done: linkedLocks > 0, href: "?tab=devices", optional: true },
    { id: "thermostats", label: "Thermostats", detail: linkedThermostats ? `${linkedThermostats} thermostat${linkedThermostats === 1 ? "" : "s"} linked to this property.` : "No thermostats linked to this property.", done: linkedThermostats > 0, href: "?tab=devices", optional: true },
    { id: "isp", label: "ISP outage information", detail: [!property.ispProvider?.trim() && "provider", !property.ispOutageUrl?.trim() && "outage URL", !property.ispLabel?.trim() && "display label"].filter(Boolean).length ? `Missing: ${[!property.ispProvider?.trim() && "provider", !property.ispOutageUrl?.trim() && "outage URL", !property.ispLabel?.trim() && "display label"].filter(Boolean).join(", ")}.` : "Provider, outage URL and label are saved.", done: Boolean(property.ispProvider?.trim() && property.ispOutageUrl?.trim() && property.ispLabel?.trim()), href: "#property-wifi", optional: true },
    { id: "floorplan", label: "Floor plan", detail: property.floorPlanUrl ? "A floor plan link is saved." : "Add a floor plan link.", done: Boolean(property.floorPlanUrl), href: "#property-overview-fields", optional: true },
    { id: "tour", label: "3D tour", detail: property.tourUrl ? "A 3D tour link is saved." : "Add a 3D tour link.", done: Boolean(property.tourUrl), href: "#property-overview-fields", optional: true },
    { id: "video", label: "Video walkthrough", detail: property.videoWalkthroughUrl ? "A video walkthrough link is saved." : "Add a hosted video link.", done: Boolean(property.videoWalkthroughUrl), href: "#property-overview-fields", optional: true },
    { id: "documents", label: "Supporting documents", detail: documents?.total ? `${documents.total} property document${documents.total === 1 ? "" : "s"} saved.` : "Attach helpful property documents.", done: Boolean(documents?.total), href: "?tab=documents", optional: true },
  ];
  return { core, optional };
}

function AvailabilityNote({ state, label, onRetry }: { state: ReadState; label: string; onRetry?: () => void }) {
  if (state === "success") return null;
  const message = state === "loading" ? `Loading ${label}…` : state === "restricted" ? `${label} is not available with your current access.` : `Couldn't load ${label}.`;
  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-100" role={state === "error" ? "alert" : undefined}>
      <span>{message}</span>
      {state === "error" && onRetry ? <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={onRetry}><RotateCw className="mr-1 h-3 w-3" />Retry</Button> : null}
    </div>
  );
}

function SetupLink(props: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  // Native fragment navigation scrolls to the editor; router pushState does not.
  return props.href?.startsWith("#") ? <a {...props} /> : <Link {...props} />;
}

export function PropertySetupChecklist(props: ChecklistProps & { onRetry?: (resource: "photo" | "rooms" | "knowledge" | "appliances" | "devices" | "documents") => void }) {
  const [localOpen, setLocalOpen] = useState(false);
  const open = props.expanded ?? localOpen;
  const setOpen = (next: boolean) => {
    setLocalOpen(next);
    props.onExpandedChange?.(next);
  };
  const items = buildPropertySetupItems(props);
  const coreKnown = props.photoState === "success" && props.roomsState === "success" && props.knowledgeBaseState === "success";
  const completed = items.core.filter((item) => item.done).length;
  const percent = coreKnown ? Math.round((completed / PROPERTY_SETUP_CORE_TOTAL) * 100) : null;
  const stateForCoreItem = (id: string): ReadState | null => {
    if (id === "photo") return props.photoState;
    if (id === "rooms" || id === "room-photos") return props.roomsState;
    if (id === "knowledge") return props.knowledgeBaseState;
    return null;
  };
  const stateForOptionalItem = (id: string): ReadState | null => {
    if (id === "appliances") return props.appliancesState;
    if (id === "locks" || id === "thermostats") return props.devicesState;
    if (id === "documents") return props.documentsState;
    return null;
  };
  const knownDone = (item: ChecklistItem) => item.done &&
    (!stateForCoreItem(item.id) || stateForCoreItem(item.id) === "success") &&
    (!stateForOptionalItem(item.id) || stateForOptionalItem(item.id) === "success");
  const checklistHref = (href: string) => href.startsWith("?") ? `${href}&checklist=open` : href;
  return (
    <section className="min-w-0 overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50/80 dark:border-emerald-900/70 dark:bg-emerald-950/25" data-testid="property-setup-checklist">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-5" data-testid="property-setup-heading-row">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.13em] text-emerald-800 dark:text-emerald-300">Set up and grow</p>
          <h2 className="mt-1 text-base font-semibold text-foreground">Property Setup Checklist</h2>
          <p className="mt-0.5 text-sm text-muted-foreground" data-testid="text-setup-progress">
            {percent === null ? "Core setup progress is being checked" : `${completed} of ${items.core.length} core items complete · ${percent}%`}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="min-h-10 cursor-pointer rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 dark:hover:bg-emerald-900/60 dark:focus-visible:ring-emerald-400" data-testid="button-toggle-setup-checklist">
            {open ? "Hide checklist" : "Show checklist"}
          </button>
          {props.actions ? <div className="flex items-center gap-2" data-testid="property-setup-actions">{props.actions}</div> : null}
        </div>
      </div>
      {open ? (
        <div className="grid gap-4 border-t border-emerald-200 bg-white/70 p-4 dark:border-emerald-900/70 dark:bg-card sm:p-5 lg:grid-cols-[minmax(0,1fr)_minmax(240px,.72fr)]">
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Core setup</h3>
            <AvailabilityNote state={props.roomsState} label="room setup" onRetry={() => props.onRetry?.("rooms")} />
            <AvailabilityNote state={props.photoState} label="property photo" onRetry={() => props.onRetry?.("photo")} />
            <AvailabilityNote state={props.knowledgeBaseState} label="Knowledge Base" onRetry={() => props.onRetry?.("knowledge")} />
            <ul className="mt-2 divide-y divide-border">
              {items.core.map((item) => (
                <li key={item.id} className="flex items-start gap-3 py-2.5" data-testid={`setup-core-${item.id}`}>
                  {knownDone(item) ? <CircleCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-400" /> : <CircleDashed aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                  <div className="min-w-0 flex-1">
                    {(() => {
                      const state = stateForCoreItem(item.id);
                      const unknown = state && state !== "success";
                      const detail = state === "loading" ? "Checking saved information…" : state === "error" ? "Saved information could not be verified. Retry above." : state === "restricted" ? "This information is unavailable with your current access." : item.detail;
                      return <>
                        {state === "restricted" ? <span className="text-sm font-medium text-muted-foreground">{item.label}</span> : <SetupLink href={checklistHref(item.href)} onClick={(event) => props.onNavigate?.(item.href, event)} className={`text-sm font-medium ${knownDone(item) ? "text-muted-foreground line-through" : "text-foreground hover:text-emerald-800 dark:hover:text-emerald-300"}`}>{item.label}</SetupLink>}
                        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{unknown ? detail : item.detail}</p>
                      </>;
                    })()}
                  </div>
                  {!knownDone(item) && stateForCoreItem(item.id) !== "restricted" ? <SetupLink href={checklistHref(item.href)} aria-label={`Complete ${item.label}`} onClick={(event) => props.onNavigate?.(item.href, event)} className="mt-0.5"><ChevronRight className="h-4 w-4 text-muted-foreground" /></SetupLink> : null}
                </li>
              ))}
            </ul>
          </div>
          <aside className="border-t border-border pt-4 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Optional recommendations</h3>
            <p className="mb-2 text-xs text-muted-foreground">Helpful extras; these never affect core setup progress.</p>
            <AvailabilityNote state={props.appliancesState} label="appliance inventory" onRetry={() => props.onRetry?.("appliances")} />
            <AvailabilityNote state={props.devicesState} label="linked devices" onRetry={() => props.onRetry?.("devices")} />
            <AvailabilityNote state={props.documentsState} label="supporting documents" onRetry={() => props.onRetry?.("documents")} />
            <ul className="divide-y divide-border">
              {items.optional.map((item) => (
                <li key={item.id} className="flex items-start gap-2 py-2" data-testid={`setup-optional-${item.id}`}>
                  {knownDone(item) ? <CircleCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-400" /> : <CircleDashed aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                  <div className="min-w-0">
                    {(() => {
                      const state = stateForOptionalItem(item.id);
                      const unknown = state && state !== "success";
                      const detail = state === "loading" ? "Checking saved information…" : state === "error" ? "Saved information could not be verified. Retry above." : state === "restricted" ? "Unavailable with your current access." : item.detail;
                      return <>
                        {state === "restricted" ? <span className="text-xs font-medium text-muted-foreground">{item.label}</span> : <SetupLink href={checklistHref(item.href)} onClick={(event) => props.onNavigate?.(item.href, event)} className={`text-xs font-medium ${knownDone(item) ? "text-muted-foreground line-through" : "text-foreground hover:text-emerald-800 dark:hover:text-emerald-300"}`}>{item.label}</SetupLink>}
                        <p className="mt-0.5 text-xs text-muted-foreground">{unknown ? detail : item.detail}</p>
                      </>;
                    })()}
                  </div>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      ) : null}
    </section>
  );
}
